import { ArrowUpCircle, CircleAlert, CircleCheck, FastForward, Info, ListChecks, PackageCheck, Pause, Play, Rocket, Settings2, ShieldAlert, Square } from '@orgus/ui/icons';
import { useState } from 'react';
import { Link } from 'react-router';
import type { FleetNotice, FleetState, FleetVersionsReport, Machine, Rollout, RolloutTarget } from '@orgus/protocol';
import { Banner, Button, ConfirmDialog, Dialog, ErrorState, IconButton, Input, Panel, RelativeTime, RowStatus, Select, Skeleton, StateBar, Tooltip, WorkStateIcon, cn, formatClock, formatInt, plural, toUiError } from '@orgus/ui';
import type { RowStatusProps, WorkState } from '@orgus/ui';
import { TARGET_LABEL, autoLine, belowMinimum, ringProgress, rolloutLabel, stageProgress, versionGroups } from './model.ts';
import { fleetActions, fleetReports } from './store.ts';
import { UpdateSettingsDialog } from './UpdateSettingsDialog.tsx';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';

/** What failed, kept inline in the panel: errors never go in a toast. */
type Failure = { title: string; error: unknown } | null;

const DAY = 86_400_000;
const who = (by: string) => (by === 'policy' ? 'automatic updates' : by);

function rolloutStatus(r: Rollout): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const label = rolloutLabel(r);
  if (r.status === 'halted') return { tone: 'danger', icon: 'failed', label };
  if (r.status === 'awaiting-promotion') return { tone: 'attention', icon: 'needs-you', label };
  if (r.status === 'cancelled') return { tone: 'neutral', icon: 'stopped', label };
  if (r.status === 'done') return r.targets.some((t) => t.state === 'failed') ? { tone: 'danger', icon: 'failed', label } : { tone: 'success', icon: 'done', label };
  if (r.plan?.soakUntil) return { tone: 'neutral', icon: 'paused', label };
  return { tone: 'working', icon: 'working', label };
}

/** A rollout target as a work state, so an update reads like any other work: waiting, updating, updated, failed. */
const TARGET_STATE: Record<RolloutTarget['state'], WorkState> = {
  pending: 'queued',
  offered: 'queued',
  updating: 'running',
  done: 'landed',
  failed: 'failed',
  skipped: 'stale',
};

/**
 * One stage or ring of a rollout: its name and count, a state bar (segments move by transform,
 * never width), and its machines as work-state chips. A stage that has not started stays grey.
 */
function Progress({ name, count, targets, machines, current, later }: { name: string; count: string; targets: RolloutTarget[]; machines: Record<string, Machine>; current?: boolean; later?: boolean }) {
  const n = (states: RolloutTarget['state'][]) => targets.filter((t) => states.includes(t.state)).length;
  const counts: Partial<Record<WorkState, number>> = later
    ? { queued: targets.length }
    : { landed: n(['done']), failed: n(['failed']), running: n(['updating']), queued: n(['pending', 'offered']), stale: n(['skipped']) };
  return (
    <li className="flex min-w-0 flex-col gap-3" aria-current={current ? 'step' : undefined}>
      <div className="flex min-w-0 items-baseline gap-2">
        <span className={cn('truncate text-ui', current ? 'font-medium text-fg' : later ? 'text-fg-subtle' : 'text-fg-muted')}>{name}</span>
        <span className="ml-auto shrink-0 text-caption text-fg-subtle num">{count}</span>
      </div>
      <StateBar counts={counts} label={`${name}: ${count}`} />
      {targets.length > 0 && (
        <ul className="flex min-w-0 flex-wrap gap-2" aria-label={`Machines in ${name.toLowerCase()}`}>
          {targets.map((t) => {
            const m = machines[t.machineId];
            const label = m?.name ?? 'Removed machine';
            const word = later ? 'Later stage' : TARGET_LABEL[t.state];
            const offline = (t.state === 'pending' || t.state === 'offered') && m && !m.online;
            return (
              <li key={t.machineId} className="motion-arrive">
                <Tooltip content={`${label}: ${word}${offline ? ', offline: it updates when it connects' : ''}${t.message ? `. ${t.message}` : ''}`}>
                  <span tabIndex={0} className="inline-flex h-control-sm items-center gap-2 rounded-pill bg-hover px-3 text-caption">
                    <WorkStateIcon state={later ? 'queued' : TARGET_STATE[t.state]} />
                    <span className="text-fg">{label}</span>
                    <span className="text-fg-subtle">{word}</span>
                  </span>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function RolloutBlock({ r, machines, held, onFail }: { r: Rollout; machines: Record<string, Machine>; held: string | null; onFail: (f: Failure) => void }) {
  const [cancelling, setCancelling] = useState(false);
  const all = stageProgress(r);
  // A stage with no machine of its own is passed over (a small fleet): only the rest are shown.
  const stages = all.filter((p) => p.total > 0 || p.index === r.plan?.wave);
  const rings = ringProgress(r);
  const offline = r.targets.filter((t) => t.state === 'pending' && (r.plan ? (t.wave ?? 99) <= r.plan.wave : t.ring === r.stage) && !machines[t.machineId]?.online).length;
  const next = r.plan ? stages.find((p) => p.index > r.plan!.wave) : undefined;
  // One short line at most: why it holds (or how long it soaks), and who is offline.
  const note = [
    held ?? (r.plan?.soakUntil != null && next ? `Soaking until ${formatClock(r.plan.soakUntil)}, then ${next.name.toLowerCase()}.` : null),
    offline > 0 ? `${plural(offline, 'machine')} offline, updated when ${offline === 1 ? 'it connects' : 'they connect'}.` : null,
  ].filter(Boolean);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <RowStatus {...rolloutStatus(r)} />
        <span className="text-ui font-medium text-fg num">Agent {r.version}</span>
        <span className="text-caption text-fg-subtle">
          Started <RelativeTime ts={r.startedAt} /> by {who(r.startedBy)}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {r.status === 'awaiting-promotion' && (
            <Button size="sm" variant="secondary" icon={<Rocket />} onClick={() => void fleetActions.promote(r.id).catch((e: unknown) => onFail({ title: 'Rollout not promoted', error: e }))}>
              Promote to the fleet
            </Button>
          )}
          {r.plan?.soakUntil != null && (
            <Button size="sm" variant="ghost" icon={<FastForward />} onClick={() => void fleetActions.promote(r.id).catch((e: unknown) => onFail({ title: 'The next stage did not start', error: e }))}>
              Start next stage
            </Button>
          )}
          <Button size="sm" variant="ghost" icon={<Square />} onClick={() => setCancelling(true)}>
            Cancel rollout
          </Button>
        </span>
      </div>
      {r.plan ? (
        <ol className={cn('grid gap-x-8 gap-y-4', stages.length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2')} aria-label="Stages">
          {stages.map((p) => (
            <Progress
              key={p.index}
              name={p.name}
              count={p.total ? `${formatInt(p.done)} of ${formatInt(p.total)}${p.failed ? `, ${formatInt(p.failed)} failed` : ''}` : 'No machines'}
              targets={r.targets.filter((t) => t.wave === p.index)}
              machines={machines}
              current={p.state === 'current' || p.state === 'soaking'}
              later={p.state === 'next'}
            />
          ))}
        </ol>
      ) : (
        <ol className="grid gap-x-8 gap-y-4 md:grid-cols-2" aria-label="Rings">
          {rings.map((p) => (
            <Progress
              key={p.ring}
              name={p.label}
              count={`${formatInt(p.done)} of ${formatInt(p.total - p.skipped)} updated${p.failed ? `, ${formatInt(p.failed)} failed` : ''}${p.skipped ? `, ${formatInt(p.skipped)} by hand` : ''}`}
              targets={r.targets.filter((t) => t.ring === p.ring)}
              machines={machines}
              current={p.ring === r.stage}
            />
          ))}
        </ol>
      )}
      {note.length > 0 && <p className="text-caption text-fg-subtle">{note.join(' ')}</p>}
      <ConfirmDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={`Cancel the rollout of agent ${r.version}?`}
        description="No more machines get the update; updated machines keep it."
        confirmLabel="Cancel rollout"
        cancelLabel="Keep rolling out"
        onConfirm={() => fleetActions.cancel(r.id)}
      />
    </div>
  );
}

const NOTICE_ICON = { success: CircleCheck, attention: CircleAlert, danger: CircleAlert, neutral: Info } as const;
const NOTICE_TONE = { success: 'text-fg-subtle', attention: 'text-status-attention', danger: 'text-status-danger', neutral: 'text-fg-subtle' } as const;

/** Quiet lines about recent rollouts; only a failure takes colour. */
function Notices({ notices }: { notices: FleetNotice[] }) {
  if (!notices.length) return null;
  return (
    <ul className="flex flex-col gap-2" aria-label="Recent updates">
      {notices.slice(0, 3).map((n) => {
        const Icon = NOTICE_ICON[n.tone];
        return (
          <li key={n.id} className="motion-arrive flex min-w-0 items-center gap-2 text-caption">
            <Icon className={cn('size-3 shrink-0', NOTICE_TONE[n.tone])} aria-hidden />
            <span className={cn('shrink-0', n.tone === 'danger' ? 'text-status-danger' : 'text-fg-muted')}>{n.title}</span>
            {n.detail && (
              <Tooltip content={n.detail}>
                <span tabIndex={0} className="min-w-0 truncate text-fg-subtle">
                  {n.detail}
                </span>
              </Tooltip>
            )}
            <RelativeTime ts={n.at} className="ml-auto shrink-0 text-fg-subtle" />
          </li>
        );
      })}
    </ul>
  );
}

const PAUSE_FOR = [
  { value: 'resume', label: 'Until I resume' },
  { value: String(DAY), label: 'For 1 day' },
  { value: String(7 * DAY), label: 'For 1 week' },
];

function PauseDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [length, setLength] = useState('resume');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const pause = async () => {
    setBusy(true);
    setError(null);
    try {
      await fleetActions.pause({ until: length === 'resume' ? null : Date.now() + Number(length), ...(reason.trim() ? { reason: reason.trim() } : {}) });
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'Automatic updates were not paused.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Pause automatic updates"
      description="No new rollout starts, and a running one offers no more machines."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" icon={<Pause />} loading={busy} onClick={() => void pause()}>
            Pause updates
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error != null && <ErrorState variant="inline" error={error} />}
        <Select label="Resume" value={length} onValueChange={setLength} options={PAUSE_FOR} />
        <Input label="Reason" hint="Shown to everyone in Machines" placeholder="Quarter close" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
      </div>
    </Dialog>
  );
}

/**
 * Machines below the hub's minimum agent version: the one loud fact about versions, because
 * write actions there are refused. One banner above the fleet, with the way out.
 */
export function BelowMinimum({ state, machines, minAgentVersion }: { state: FleetState | null; machines: Machine[]; minAgentVersion: string | null }) {
  const [failure, setFailure] = useState<Failure>(null);
  const below = machines.filter((m) => belowMinimum(m.agentVersion, minAgentVersion));
  if (!minAgentVersion || !below.length) return null;
  const latest = state?.latest;
  const updateBelow = () => {
    if (!state) return;
    const fm = new Map(state.machines.map((x) => [x.machineId, x]));
    const can = below.filter((m) => m.online && fm.get(m.id)?.updatable !== false);
    if (!can.length) {
      setFailure({ title: 'Nothing to update now', error: { what: 'Nothing to update now.', why: 'Those machines are offline or cannot update themselves.', next: 'Update them by hand: pull and restart Orgus there.' } });
      return;
    }
    for (const m of can) void fleetActions.updateMachine(m.id).catch((e: unknown) => setFailure({ title: `${m.name} not updated`, error: e }));
  };
  return (
    <Banner
      tone="danger"
      icon={<ShieldAlert />}
      title={`${below.map((m) => m.name).join(', ')} ${below.length === 1 ? 'runs' : 'run'} an agent below ${minAgentVersion}`}
      actions={
        <>
          <Link to="/settings/trust" className="px-2 text-ui text-fg-muted underline-offset-2 hover:text-fg hover:underline">
            Change the minimum
          </Link>
          {latest && !state?.rollout && (
            <Button size="sm" variant="secondary" icon={<ArrowUpCircle />} onClick={updateBelow}>
              Update {below.length === 1 ? 'it' : 'them'}
            </Button>
          )}
        </>
      }
    >
      Write actions there are refused until {below.length === 1 ? 'it updates' : 'they update'}; reads keep working.
      {failure && <ErrorState variant="inline" error={toUiError(failure.error)} />}
    </Banner>
  );
}

/**
 * Agent versions and updates as one quiet panel: which versions run where and the latest release
 * in the header, with what automatic updates are doing and their controls; under it, while a
 * rollout runs, each stage or ring as a state bar with its machines, else quiet notices about
 * recent rollouts. The below-minimum warning is its own banner (BelowMinimum).
 */
export function FleetSummary({ state, loading, error, onRetry, machines }: { state: FleetState | null; loading: boolean; error: unknown; onRetry: () => Promise<void>; machines: Machine[] }) {
  const [rolling, setRolling] = useState(false);
  const [settings, setSettings] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [failure, setFailure] = useState<Failure>(null);
  const [checked, setChecked] = useState<FleetVersionsReport | null>(null);
  const [checking, setChecking] = useState(false);
  // On a phone the controls move into the body, so the header keeps its title; they render once either way.
  const wide = !useMediaQuery(NARROW_QUERY);
  const byId = Object.fromEntries(machines.map((m) => [m.id, m]));
  if (!state && loading) {
    return (
      <Panel title="Agent versions" icon={<PackageCheck />} aria-busy="true" aria-label="Loading agent versions">
        <Skeleton className="h-3 w-64" />
      </Panel>
    );
  }
  if (!state) {
    return (
      <Panel title="Agent versions" icon={<PackageCheck />}>
        <ErrorState variant="inline" error={error ?? new Error('The hub sent no fleet state.')} onRetry={onRetry} />
      </Panel>
    );
  }

  const now = Date.now();
  const latest = state.latest;
  const groups = versionGroups(machines, latest);
  const behind = machines.filter((m) => latest && m.agentVersion && groups.find((g) => g.version === m.agentVersion)?.drift === 'behind');
  const top = state.releases.find((r) => r.version === latest) ?? state.releases[0];
  const versions = groups.map((g) => `${g.version} on ${formatInt(g.count)}`).join(', ');
  const auto = autoLine(state, now);
  const paused = !!state.policy?.paused && (state.policy.paused.until === null || state.policy.paused.until > now);
  const automatic = state.policy?.mode === 'auto' && !paused;
  const hold = !state.rollout && state.auto?.hold && behind.length ? state.auto.hold : null;
  const notices = state.notices ?? [];
  const last = state.history[0];
  const held =
    state.rollout && state.rollout.startedBy === 'policy' && (paused || auto.label === 'Update freeze')
      ? `${auto.label}: no more machines are offered it until ${auto.label === 'Update freeze' ? 'the freeze ends' : 'updates resume'}.`
      : null;
  const hasBody = !!(hold || state.rollout || notices.length || last || failure || checked);
  const checkVersions = async () => {
    setChecking(true);
    setFailure(null);
    try {
      setChecked(await fleetReports.checkVersions());
    } catch (e) {
      setFailure({ title: 'Versions not checked', error: e });
    } finally {
      setChecking(false);
    }
  };
  const controls = (
    <>
      {state.policy && state.feed && (
        <Tooltip content={[auto.detail, auto.until ? `Until ${formatClock(auto.until)}` : null].filter(Boolean).join('. ') || auto.label}>
          <span tabIndex={0} className="flex items-center pr-1">
            <RowStatus tone={auto.tone} icon={auto.icon} label={auto.label} />
          </span>
        </Tooltip>
      )}
      {!automatic && !state.rollout && behind.length > 0 && latest && state.feed && (
        <Button size="sm" variant="secondary" icon={<Rocket />} onClick={() => setRolling(true)}>
          Roll out {latest}
        </Button>
      )}
      {state.policy &&
        state.feed &&
        (paused ? (
          <Button size="sm" variant="ghost" icon={<Play />} onClick={() => void fleetActions.resume().catch((e: unknown) => setFailure({ title: 'Updates not resumed', error: e }))}>
            Resume updates
          </Button>
        ) : state.policy.mode === 'auto' ? (
          <Button size="sm" variant="ghost" icon={<Pause />} onClick={() => setPausing(true)}>
            Pause updates
          </Button>
        ) : null)}
      <IconButton size="sm" variant="ghost" label="Check versions" icon={<ListChecks />} disabled={checking} onClick={() => void checkVersions()} />
      {state.policy && state.feed && <IconButton size="sm" variant="ghost" label="Update settings" icon={<Settings2 />} onClick={() => setSettings(true)} />}
    </>
  );

  return (
    <Panel
      title="Agent versions"
      icon={<PackageCheck />}
      aria-label="Agent versions and updates"
      sub={
        <Tooltip content={groups.map((g) => `${g.version}: ${g.names.join(', ')}`).join('. ')}>
          <span tabIndex={0}>
            {versions || 'No machines yet'}
            <span className="px-2 text-fg-faint" aria-hidden>
              /
            </span>
            {top ? (
              <>
                Latest <span className="text-fg-muted">{top.version}</span>, <RelativeTime ts={top.createdAt} />
              </>
            ) : (
              'Updated by hand on each machine'
            )}
          </span>
        </Tooltip>
      }
      actions={wide ? <span className="flex items-center justify-end gap-2">{controls}</span> : undefined}
      bodyClassName={hasBody || !wide ? undefined : 'hidden'}
    >
      <div className="flex flex-col gap-4">
        {!wide && <div className="flex flex-wrap items-center gap-2">{controls}</div>}
        {hold && (
          <p className="flex min-w-0 items-center gap-2 text-caption text-fg-muted">
            <Info className="size-3 shrink-0 text-fg-subtle" aria-hidden />
            {state.auto?.next ? <span className="shrink-0 text-fg num">Agent {state.auto.next} waits</span> : null}
            <span className="min-w-0 truncate">{hold.detail}</span>
            {hold.until ? <span className="shrink-0 text-fg-subtle">Until {formatClock(hold.until)}</span> : null}
          </p>
        )}
        {state.rollout && <RolloutBlock r={state.rollout} machines={byId} held={held} onFail={setFailure} />}
        {!state.rollout && notices.length > 0 && <Notices notices={notices} />}
        {/* While a rollout runs, only a notice that needs a person: the server and the official release disagree. */}
        {state.rollout && notices.some((n) => n.tone === 'danger') && <Notices notices={notices.filter((n) => n.tone === 'danger')} />}
        {!state.rollout && !notices.length && last && (
          <p className="text-caption text-fg-subtle">
            Last rollout: agent {last.version}, {rolloutLabel(last).toLowerCase()} <RelativeTime ts={last.endedAt ?? last.startedAt} />
            {last.note ? `. ${last.note}` : ''}
          </p>
        )}
        {checked && (
          <p className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
            <ListChecks className="size-3 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">
              <span className="text-fg-muted">{checked.behind ? `${formatInt(checked.behind)} behind` : 'None behind'}</span>
              {checked.versions.length ? `, ${checked.versions.map((v) => `${v.version} on ${formatInt(v.machines)}`).join(', ')}` : ''}
            </span>
            <RelativeTime ts={checked.at} prefix="Checked" className="ml-auto shrink-0" />
          </p>
        )}
        {failure && <ErrorState variant="inline" error={toUiError(failure.error, { what: failure.title })} />}
      </div>

      <ConfirmDialog
        open={rolling}
        onOpenChange={setRolling}
        tone="primary"
        title={`Roll out agent ${latest}?`}
        description={
          state.machines.some((m) => m.ring === 'test' && behind.some((b) => b.id === m.machineId))
            ? `Machines in the test ring update first. The other ${plural(behind.filter((b) => state.machines.find((m) => m.machineId === b.id)?.ring !== 'test').length, 'machine')} wait until you promote it. Each machine waits for its running commands and headless sessions before it restarts, and rolls itself back if the new version does not start.`
            : `No machine is in the test ring, so all ${plural(behind.length, 'machine')} update at once. Put one machine in the test ring first (in its details) to try new versions there. Each machine rolls itself back if the new version does not start.`
        }
        confirmLabel="Start rollout"
        onConfirm={() => (latest ? fleetActions.startRollout(latest) : undefined)}
      />
      {state.policy && <UpdateSettingsDialog open={settings} onOpenChange={setSettings} policy={state.policy} />}
      <PauseDialog open={pausing} onOpenChange={setPausing} />
    </Panel>
  );
}
